import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import {
  Theme,
  createStyles,
  WithStyles,
  withStyles,
  Grid,
  Card,
  CardContent,
  Typography,
  CardActions,
  Link,
} from '@material-ui/core';
import { NavigateNextOutlined as NextIcon } from '@material-ui/icons';
import { MyPATIconButton } from '../../common';
import TagPreview from './TagPreview';

import { ContentTagsBody } from '../types/ContentTagsBody';
import { CourseTagsBody } from '../types/CourseTagsBody';
import ContentTagsActions from '../actions/ContentTagsActions';
import CourseTagsActions from '../actions/CourseTagActions';
import { TagTypesBody } from '../types/TagTypesBody';
import ACL from '../../acl/components/ACL';
import CustomInfoIcon from '../../common/CustomInfoIcon';
import IconButton from '@material-ui/core/IconButton';
import ACLService from '../../acl/services/aclService';

const styles = (theme: Theme) => createStyles({
  mb2x: {
    marginBottom: theme.spacing.unit * 2
  },
  mr2x: {
    marginRight: theme.spacing.unit * 2,
  },
  mb1x: {
    marginBottom: theme.spacing.unit,
  },
  fwNormal: {
    fontWeight: 'normal',
  },
  textCapitalize: {
    textTransform: 'capitalize',
  },
  card: {
    background: '#FFFFFF',
    border: '1px solid #E1E1E1',
    boxSizing: 'border-box',
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.12)',
    borderRadius: 4
  },
  cardContentRoot: {
    ...theme.mixins.gutters(),
    paddingTop: theme.spacing.unit * 2,
    paddingBottom: theme.spacing.unit / 2
  },
  lightGrey: {
    background: '#F5F5F5'
  },
  borderSm: {
    borderRadius: '0px 0px 3px 3px'
  },
  textGrey: {
    color: '#666666'
  },
  lineHeightSM: {
    lineHeight: '20px'
  },
  fontWeightSM: {
    fontWeight: 600,
  },
  fontFamily: {
    fontFamily: 'Nunito',
    fontStyle: 'normal',
  },
  fontXS: {
    fontSize: 14,
    letterSpacing: 0.260695,
  },
  fontSM: {
    fontSize: 16,
    letterSpacing: 0.297937
  },
  fontMD: {
    fontSize: 20,
    fontWeight: 600
  },
  lineHeightMD: {
    lineHeight: '36px'
  },
  marginBottomXS: {
    marginBottom: 8
  },
  paddingSM: {
    padding: '6px 20px'
  },
  paddingMD: {
    padding: '9px 2px'
  },
  paddingLeftSM: {
    paddingLeft: 40
  },
  paddingLeftMD: {
    paddingLeft: 123
  },
  paddingRightMD: {
    paddingRight: 100
  },
  tooltipDetails: {
    position: 'absolute',
    marginTop: 15,
    zIndex: 999999
  },
  disable: {
    pointerEvents: "none",
    opacity: 0.5,
    cursor: "default"
  },
  updateIcon: {
    '&:hover:after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      marginLeft: -5,
      bottom: -8,
      width: 0,
      height: 0,
      borderLeft: '5px solid rgba(0, 0, 0, 0)',
      borderRight: '5px solid rgba(0, 0, 0, 0)',
      borderBottom: '8px solid rgb(0, 0, 0)'
    },
    '&:hover .tooltipContainer': {
      display: 'block',
      cursor: 'pointer',
      marginTop: 2,
      marginLeft: 10
		},
		"& .tooltipContainer": {
      display: 'none',
      position: 'absolute',
      margin: '4px -40px',
      zIndex: 999,
      top: '100%'
		},
  }
});

interface TagInfoCardProps extends WithStyles<typeof styles> {
  tagInfo: ContentTagsBody | CourseTagsBody,
  contentTagTypes: TagTypesBody[],
  courseTagTypes: TagTypesBody[],
  onEditClick: (tagId?: string) => void,
  onHover: () => void,
  onMouseOut: () => void,
  isTooltip: boolean,
  userData: any
}

interface TagInfoCardState {
  showTooltip: boolean
}



const TagInfoCard = (props: TagInfoCardProps, state: TagInfoCardState) => {
  const {
    classes,
    tagInfo,
    contentTagTypes,
    courseTagTypes,
    onEditClick,
    onHover,
    isTooltip,
    onMouseOut,
    userData
  } = props;

  const { showTooltip } = state;

  return (
    <>
      <Card className={[classes.mb2x, classes.card].join(' ')}>
        <CardContent className={classes.cardContentRoot}>
          <Grid container alignItems="center">
            <Grid item xs>
              <Grid container alignItems="center">
                <Grid item xs={3} zeroMinWidth className={[classes.paddingMD, classes.paddingRightMD].join(' ')}>
                  <Typography variant="body1" className={[classes.fontSM, classes.textGrey, classes.fontFamily, classes.fontWeightSM, classes.lineHeightSM].join(' ')}>Name</Typography>
                  <Typography className={[classes.fontMD, classes.fontFamily, classes.lineHeightMD, classes.fontWeightSM].join(' ')} variant="body1" noWrap>
                    {tagInfo.name}
                  </Typography>
                </Grid>
                <Grid item xs={3} className={classes.paddingMD}>
                  <Typography variant="body1" className={[classes.fontSM, classes.textGrey, classes.fontFamily, classes.fontWeightSM, classes.lineHeightSM].join(' ')}>Tag Type</Typography>
                  <Typography variant="body1" className={[classes.fontMD, classes.lineHeightMD, classes.fontFamily, classes.fontWeightSM, classes.textCapitalize].join(' ')} noWrap> {_.get(_.find(contentTagTypes, ['type', tagInfo.content.type]), 'title', tagInfo.content.type)} </Typography>
                </Grid>
                <Grid item xs={2} className={[classes.paddingMD, classes.paddingLeftSM].join(' ')}>
                  <Typography className={[classes.fontSM, classes.textGrey, classes.fontFamily, classes.fontWeightSM, classes.lineHeightSM].join(' ')}>Preview</Typography>
                  <TagPreview className={[classes.fontMD, classes.fontFamily, classes.lineHeightMD, classes.fontWeightSM, classes.marginBottomXS].join(' ')} tagType={tagInfo.content.type} />
                </Grid>
                <Grid item xs={3} className={[classes.paddingMD, classes.paddingLeftMD].join(' ')}>
                  <Typography variant="body1" className={[classes.fontSM, classes.textGrey, classes.fontFamily, classes.fontWeightSM, classes.lineHeightSM].join(' ')}>Tag Priority</Typography>
                  <Typography variant="body1" className={[classes.fontMD, classes.fontFamily, classes.lineHeightMD, classes.fontWeightSM].join(' ')}> {tagInfo.isCompulsory ? 'Mandatory' : 'Optional'} </Typography>
                </Grid>
              </Grid>
            </Grid>

              <Grid item xs="auto">
                <IconButton
                  component="span"
                  onClick={() => onEditClick(tagInfo._id)}
                  onMouseOver={() => onHover()}
                  onMouseOut={() => onMouseOut()}
                  className={`${classes.updateIcon} ${!ACLService.checkModulePermission("tag", "edit") ? classes.disable : ""}`}                  
                >
                  <img src="/assets/svg/edit-icon.svg" alt="edit" />
                  <div className="tooltipContainer">
                  <CustomInfoIcon
                    isInfoIcon={false}
                    isCenterArrow={true}
                    isRightArrow={true}
                    knowMoreAbout={false}
                    details="Update Tag"
                    toolTipDirection="top" />
						    </div>
                </IconButton>
                
                {/* <span className={classes.tooltipDetails} className={classes.toolTipArrow}>
                  {isTooltip ?
                    <CustomInfoIcon
                      isInfoIcon={false}
                      knowMoreAbout={false}
                      isCenterArrow={true}
                      isRightArrow={false}
                      details="Update tag" /> : ''
                  }
                </span> */}
              </Grid>
          </Grid>
        </CardContent>
        <CardActions className={[classes.lightGrey, classes.paddingSM].join(' ')}>
          <Grid>
            <Typography variant="subtitle2" className={[classes.fontXS, classes.textGrey, classes.fontFamily, classes.fontWeightSM, classes.lineHeightSM].join(' ')} inline>
              Description:&nbsp;
                </Typography>
            <Typography variant="body2" inline className={[classes.fontXS, classes.fontFamily, classes.fwNormal, classes.lineHeightSM, classes.fwNormal].join(' ')}>
              {tagInfo.desc}
            </Typography>
          </Grid>
        </CardActions>
      </Card>
    </>
  );
};



const mapStateToProps = (state: any) => ({
  contentTagTypes: _.get(state, 'tagManagementData.contentTags.tagTypes', []),
  courseTagTypes: _.get(state, 'tagManagementData.courseTags.tagTypes', []),
  userData: state.loginData.userData

});

const mapDispatchToProps = (dispatch: any) => bindActionCreators({
  contentTagTypesList: ContentTagsActions.contentTagTypesList,
  courseTagTypesList: CourseTagsActions.courseTagTypesList,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(TagInfoCard));